import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  OnDestroy,
  OnInit
} from "@angular/core";
import { BehaviorSubject, combineLatest, Subscription } from "rxjs";
import { AdvParkingLot } from "@onsip/common/services/api/resources/parkingLot/parking-lot";
import { AdvParkService } from "@onsip/common/services/api/resources/parkingLot/parking-lot.service";
import { map } from "rxjs/operators";
import { UserAliasService } from "@onsip/common/services/api/resources/userAlias/user-alias.service";
import { AppAddressService } from "@onsip/common/services/api/resources/appAddress/app-address.service";
import { SnackbarService } from "@onsip/web/features/shared/components/snackbar/snackbar.service";
import { BreakpointObserver } from "@angular/cdk/layout";
import { DynamicPricingService } from "@onsip/common/services/dynamic-pricing.service";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { AppAddress } from "@onsip/common/services/api/resources/appAddress/app-address";
import { FailoverAddress } from "@onsip/common/services/api/apiResponse/address";
import {
  AddressSummary,
  AddressSummaryService
} from "@onsip/common/services/api/resources/addressSummary/address-summary.service";
import { AdminTableAbstractComponent } from "@onsip/web/features/abstractClass/admin-table-abstract.component";
import { E2eLocators } from "@onsip/e2e/configs/locators";
import { FormatExtensionsPipe } from "@onsip/web/features/shared/pipes/format-extensions.pipe";

export interface ParkingLotWithExt extends AdvParkingLot {
  extensions: Array<string>;
  retrieverExtensions: Array<string>;
  address: FailoverAddress;
  retrieverAddress: FailoverAddress;
  failover: AddressSummary;
}

@Component({
  selector: "onsip-parking-lot-page",
  templateUrl: "./parking-lot-page.component.html",
  styleUrls: ["./parking-lot-page.component.scss"],
  providers: [FormatExtensionsPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ParkingLotPageComponent
  extends AdminTableAbstractComponent<ParkingLotWithExt>
  implements OnInit, OnDestroy
{
  @HostBinding("class.onsip-admin-host-grid")
  @HostBinding("class.onsip-grid-content")
  _dontUse = true;

  defaultSortingColumn = "name";
  hasParkingLots = new BehaviorSubject<boolean>(false);
  pricingText = "";
  /** price of number of channels. Fetch here so we do not fetch multiple times in the shelves */
  parkChannelPrice = "0";

  protected displayedColumnsRecord = {
    XSmall: ["name", "actions"],
    Small: ["name", "extensions", "actions"],
    Medium: ["name", "extensions", "retrievalExtensions", "actions"],
    Large: ["name", "extensions", "retrievalExtensions", "actions"],
    XLarge: ["name", "extensions", "retrievalExtensions", "actions"]
  };
  E2eLocators = E2eLocators;
  protected unsubscriber = new Subscription();
  constructor(
    private parkingLotService: AdvParkService,
    private userAliasService: UserAliasService,
    private appAddressService: AppAddressService,
    private snackbar: SnackbarService,
    private dynamicPricing: DynamicPricingService,
    private addressSummaryService: AddressSummaryService,
    private formatExtensions: FormatExtensionsPipe,
    protected translate: TranslateService,
    protected breakpointObserver: BreakpointObserver,
    protected cdr: ChangeDetectorRef,
    protected dialog: MatDialog
  ) {
    super(breakpointObserver, cdr, dialog, translate);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.setDataFilter();
    this.setPricing();

    this.unsubscriber.add(
      this.dynamicPricing.getPackageInfo$("AdvParkChannel").subscribe(res => {
        if (res && res.featurePricing) {
          this.parkChannelPrice = res.featurePricing;
        }
      })
    );
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.unsubscriber.unsubscribe();
  }

  trackById(index: number, item: ParkingLotWithExt): string | undefined {
    return item?.advParkId;
  }

  protected initDataSource(): void {
    this.parkingLotService.advParkBrowse();
    this.userAliasService.userAliasBrowse();
    this.appAddressService.appAddressBrowse();
    this.addressSummaryService.addressSummaryBrowse();

    this.unsubscriber.add(
      combineLatest({
        parkingLots: this.parkingLotService.orgState,
        userAliases: this.userAliasService.orgState,
        appAddresses: this.appAddressService.orgState,
        addressSummary: this.addressSummaryService.orgState
      })
        .pipe(
          map(({ parkingLots, userAliases, appAddresses, addressSummary }) => {
            let lotsAddresses: Record<string, AppAddress> = {};
            parkingLots.forEach(
              lot =>
                (lotsAddresses = {
                  ...lotsAddresses,
                  [lot.advParkId]: appAddresses.find(
                    appAddress => appAddress.appId === lot.appId
                  ) as AppAddress,
                  [lot.advParkRetrieverAppId]: appAddresses.find(
                    appAddress => appAddress.appId === lot.advParkRetrieverAppId
                  ) as AppAddress
                })
            );

            return parkingLots.map(lot => ({
              ...lot,
              failover: addressSummary.find(address => address?.addressId === lot?.returnAddressId),
              address: lotsAddresses[lot.advParkId]?.address,
              retrieverAddress: lotsAddresses[lot.advParkRetrieverAppId]?.address,
              extensions: userAliases
                .filter(
                  alias => alias.addressId === lotsAddresses[lot.advParkId]?.address.addressId
                )
                .map(alias => alias.aliasUsername),
              retrieverExtensions: userAliases
                .filter(
                  alias =>
                    alias.addressId === lotsAddresses[lot.advParkRetrieverAppId]?.address.addressId
                )
                .map(alias => alias.aliasUsername)
            })) as Array<ParkingLotWithExt>;
          })
        )
        .subscribe(parkingLotsWithExt => {
          this.dataSource.data = parkingLotsWithExt;
          if (this.dataSource.data.length) {
            this.hasParkingLots.next(true);
          }
          this.isDataObtained$.next(true);
        })
    );
  }

  protected getItemValueByColumn(item: ParkingLotWithExt, columnName: string): string | number {
    switch (columnName) {
      case "name":
        return item.name.toLowerCase();
      case "extensions":
        return item.extensions?.join(",").toLowerCase();
      case "retrievalExtensions":
        return item.retrieverExtensions?.join(",").toLowerCase();
      default:
        return item.name.toLowerCase();
    }
  }

  protected performDeleteRequest({ advParkId }: ParkingLotWithExt): void {
    this.parkingLotService.advParkDelete(advParkId).then(res => {
      if (res.status === "success") {
        this.snackbar.openSnackBar("Parking lot successfully deleted", "success");
        this.setPricing();
        if (!this.dataSource.data.length) {
          this.hasParkingLots.next(false);
        }
      } else {
        this.snackbar.openSnackBar(res.data.message, "error");
        this.parkingLotService.clearErrors();
      }
    });
  }

  protected getItemName({ name }: ParkingLotWithExt): string {
    return name;
  }

  protected setDataFilter(): void {
    this.dataSource.filterPredicate = (data, filter) => {
      const filterValue = filter.trim().toLowerCase();
      // name
      const name = data.name;
      // extensions
      const ext = this.formatExtensions.transform(data.extensions).join(", ");
      // retrevial extensions
      const retrevial = this.formatExtensions.transform(data.retrieverExtensions).join(", ");

      return (
        name.toLowerCase().includes(filterValue) ||
        ext.toLowerCase().includes(filterValue) ||
        retrevial.toLowerCase().includes(filterValue)
      );
    };
  }

  private setPricing(): void {
    this.dynamicPricing.getDynamicPricing("AdvParkChannel", false).then(result => {
      this.pricingText = result;
      this.cdr.markForCheck();
    });
  }
}
